import { useCallback } from 'react';
import { useToast } from '@shared/components/Toast';
import { ValidateFilesFn } from '@shared/scenes/upload/hooks';
import { MBSelectAttachmentType } from '@shared/scenes/upload/util/types';
import { useDispatch, useSelector } from '@shared/store';
import { i18n } from '@shared/util/i18n';
import { useNavigator } from '@shared/util/navigation';
import { PlusButton } from '../components/plusButton/PlusButton';
import { useAskWithChatDraft } from '../hooks/useAskChatCommand';
import * as Actions from '../store/actions';
import { MAX_CHAT_ATTACHMENTS } from '../store/reducer';

interface Props {
  channelSlug?: string;
  draftId: string;
  hideUrgent?: boolean;
  /** What the user has typed so far, carried into Ask's composer by the Ask item. */
  draftText: string;
  validateUploads: ValidateFilesFn;
}

export function MBChatInputPlusButton({
  channelSlug,
  draftId,
  hideUrgent,
  draftText,
  validateUploads,
}: Props) {
  const dispatch = useDispatch();
  const { navigate } = useNavigator();
  const toast = useToast();
  const urgent = useSelector(state => !!state.chatInput.urgentFromDraftId[draftId]);

  const onLimitExceeded = useCallback(
    (rejectedCount: number) => {
      toast({
        style: 'warning',
        message: i18n('chat.input.attachmentLimit.exceeded', {
          max: MAX_CHAT_ATTACHMENTS,
          count: rejectedCount,
        }),
        duration: 4000,
      });
    },
    [toast],
  );

  const { isAvailable: isAskAvailable, askWithDraft } = useAskWithChatDraft(channelSlug);
  const onAsk = useCallback(() => askWithDraft(draftText), [askWithDraft, draftText]);

  const onCreateCard = useCallback(() => {
    if (!channelSlug) {
      return;
    }
    navigate('CardCreate', { channelSlug });
  }, [navigate, channelSlug]);

  const onShowGIFModal = useCallback(
    () => dispatch(Actions.showGIFModal(draftId)),
    [dispatch, draftId],
  );
  const onSelectAttachment = useCallback(
    (type: MBSelectAttachmentType) =>
      dispatch(Actions.selectAttachment(type, draftId, validateUploads, onLimitExceeded)),
    [dispatch, draftId, validateUploads, onLimitExceeded],
  );
  const onSendAsUrgent = useCallback(
    () => dispatch(Actions.enableUrgent(draftId)),
    [dispatch, draftId],
  );
  const onRemoveSendAsUrgent = useCallback(
    () => dispatch(Actions.disableUrgent(draftId)),
    [dispatch, draftId],
  );

  return (
    <PlusButton
      draftId={draftId}
      urgent={urgent}
      hideUrgent={hideUrgent}
      onAsk={isAskAvailable ? onAsk : undefined}
      onCreateCard={onCreateCard}
      onShowGIFModal={onShowGIFModal}
      onSelectAttachment={onSelectAttachment}
      onSendAsUrgent={onSendAsUrgent}
      onRemoveSendAsUrgent={onRemoveSendAsUrgent}
    />
  );
}
